import type { EntityID } from '@wovin/core/applog'
import type { Thread } from '@wovin/core/thread'
import type { ParentComponent } from 'solid-js'
import { joinThreads } from '@wovin/core/applog'
import { query, threadFromMaybeArray } from '@wovin/core/query'
import { getAgents, ThreadInMemory } from '@wovin/core/thread'
import { Logger } from 'besonders-logger'
import { createEffect, createMemo, createResource, Match, Switch } from 'solid-js'
import { createPreviewState } from '../ipfs/store-sync'
import { ThreadContext, useRawThread } from '../ui/reactive'
import { makeNote3Url } from '../ui/utils-ui'
import { useRel } from '../data/VMs/RelationVM'
import { Iconify } from './mini-components'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.INFO) // eslint-disable-line unused-imports/no-unused-vars

/**
 * Check if a relation has a portal/share/include applog
 * @returns The k51... IPNS name if found, null otherwise
 */
export function usePortalShareInclude(relationID: EntityID) {
	const relVM = useRel(relationID)
	return createMemo(() => {
		const val = relVM.portalShareInclude
		DEBUG(`[usePortalShareInclude] relationID=${relationID}`, { val })
		return val
	})
}

/**
 * Load a share and join it with the parent thread, including agent logs.
 * Returns a read-only thread to prevent editing external share content.
 */
async function loadShareThread(pubID: string, parentThread: Thread): Promise<Thread> {
	DEBUG(`[loadShareThread] Loading share:`, pubID)
	const previewState = createPreviewState(parentThread, pubID)
	await previewState.load()
	const shareThread = previewState.thread
	DEBUG(`[loadShareThread] Loaded share with ${shareThread.applogs.length} applogs`)

	// Join the share thread with the parent thread
	let joinedThread = joinThreads([parentThread, shareThread])

	// Add agent logs so agent names/identities resolve properly (like getShareThread does)
	const agentHashes = getAgents(joinedThread)
	const agentLogs = query(parentThread, { en: (en: EntityID) => agentHashes.includes(en), vl: 'agent/appAgent' }).leafNodeLogs
	const agentLogsToAdd = agentLogs.filter(l => !joinedThread.hasApplog(l, true))
	if (agentLogsToAdd.length > 0) {
		joinedThread = joinThreads([joinedThread, threadFromMaybeArray(agentLogsToAdd, 'portalShare.agents')])
		DEBUG(`[loadShareThread] Added ${agentLogsToAdd.length} agent logs`)
	}

	DEBUG(`[loadShareThread] Joined thread has ${joinedThread.applogs.length} applogs`)

	// Make the thread read-only to prevent editing external share content
	// HACK: Create a new read-only thread from the joined applogs
	const readOnlyThread = ThreadInMemory.fromArray(
		[...joinedThread.applogs],
		`portal-${pubID}`,
		true, // readOnly = true
	)

	return readOnlyThread
}

export interface PortalShareWrapperProps {
	relationID: EntityID
	children: any
}

/**
 * Wrapper component that provides a joined thread context when a relation
 * has a portal/share/include applog pointing to a k51... IPNS name.
 *
 * The child block will be rendered with a read-only thread that combines:
 * - The current parent thread
 * - The fetched share content
 * - Agent logs for proper identity resolution
 */
export const PortalShareWrapper: ParentComponent<PortalShareWrapperProps> = (props) => {
	const portalShareInclude = usePortalShareInclude(props.relationID)
	const parentThread = useRawThread()

	// Create a resource that loads the share thread when needed
	// TODO: Add IPNS watching for real-time updates (like usePreviewWithAutoPull does)
	//       Would need useIpnsWatcher + createPreviewState.update() for incremental updates
	const [joinedThread] = createResource(
		() => portalShareInclude(),
		async (pubID) => {
			if (!pubID) return null
			try {
				return await loadShareThread(pubID, parentThread)
			} catch (err) {
				ERROR(`[PortalShareWrapper] Failed to load share:`, pubID, err)
				return null
			}
		},
	)

	createEffect(() => {
		const pub = portalShareInclude()
		if (pub) {
			LOG(`[PortalShareWrapper] Portal share include detected:`, pub)
		}
	})

	return (
		<Switch>
			{/* No portal share include - render children directly */}
			<Match when={!portalShareInclude()}>
				{props.children}
			</Match>

			{/* Loading state */}
			<Match when={portalShareInclude() && joinedThread.loading}>
				<div class='border-1 border-solid border-purple-300 rounded-md bg-purple-50/50 my-1'>
					<div flex='~ items-center justify-between' class='px-1.5 py-0.5 mb-1 rounded-t bg-purple-100'>
						<span class='text-2.5 text-purple-600 font-500 flex-1' title={portalShareInclude()}>
							Portal: ...{portalShareInclude()?.slice(-7)}
						</span>
						<span class='text-xs text-purple-600 font-500 ml-2 animate-pulse'>Loading...</span>
					</div>
					<div opacity-60>
						{props.children}
					</div>
				</div>
			</Match>

			{/* Error state - still show children with error indicator */}
			<Match when={portalShareInclude() && joinedThread.error}>
				<div class='border-1 border-solid border-red-400 rounded-md bg-red-50/50 my-1'>
					<div flex='~ items-center justify-between' class='px-1.5 py-0.5 mb-1 rounded-t bg-red-100'>
						<span class='text-2.5 text-red-600 font-500 flex-1' title={portalShareInclude()}>
							Portal: ...{portalShareInclude()?.slice(-7)}
						</span>
						<span class='text-xs text-red-500 ml-2'>Failed to load</span>
					</div>
					{props.children}
				</div>
			</Match>

			{/* Loaded state - provide joined thread context */}
			<Match when={portalShareInclude() && joinedThread()}>
				<div class='border-1 border-solid border-purple-500 rounded-md bg-purple-50/50 my-1'>
					<div flex='~ items-center justify-between' class='px-1.5 py-0.5 mb-1 rounded-t bg-purple-100'>
						<span class='text-2.5 text-purple-600 font-500 flex-1' title={portalShareInclude()}>
							Portal: ...{portalShareInclude()?.slice(-7)}
						</span>
						<a
							href={makeNote3Url({ share: portalShareInclude(), previewPub: true })}
							target='_blank'
							title='Open portal share in new tab'
							flex='~ items-center'
							class='p-0.5 ml-2 rounded hover:bg-purple-200 cursor-pointer'
						>
							<Iconify name='eye' size='4' />
						</a>
					</div>
					<ThreadContext.Provider value={() => joinedThread()}>
						{props.children}
					</ThreadContext.Provider>
				</div>
			</Match>

			{/* Fallback - loaded but no thread (error case) */}
			<Match when={portalShareInclude() && !joinedThread()}>
				<div class='border-1 border-solid border-gray-300 rounded-md my-1'>
					{props.children}
				</div>
			</Match>
		</Switch>
	)
}

